import {
    giftRecipientsAtom,
    MAX_GIFT_RECIPIENTS,
} from '@activities/gift/GiftOrderActivity/atoms/giftOrderAtoms';
import { toPhone } from '@constants/regexp';
import useUser from '@hooks/auth/useUser';
import GiftOrderContext from '@providers/GiftOrderProvider/GiftOrderContext';
import { useAtom } from 'jotai';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import useContact from './useContact';

function useGiftRecipients() {
    const { t } = useTranslation('gift');
    const { user } = useUser();
    const { state } = useContext(GiftOrderContext);
    const [giftRecipients, setGiftRecipients] = useAtom(giftRecipientsAtom);

    const selfPhone = state.userPhone;
    const selfName = user?.identityInfo?.name || user?.nickname || state.userName;
    const isGiftToMe = !!selfPhone && giftRecipients.some((r) => toPhone(r.phoneNo) === selfPhone);

    const addRecipient = (name: string, phoneNo: string) => {
        const formatted = toPhone(phoneNo.replace(/\D/g, ''));
        if (!formatted) return;
        setGiftRecipients((prev) => {
            if (prev.length >= MAX_GIFT_RECIPIENTS) {
                toast.error(t('giftOrder.recipientLimit', { count: MAX_GIFT_RECIPIENTS }));
                return prev;
            }
            if (prev.some((r) => r.phoneNo === formatted)) {
                toast.error(t('giftOrder.recipientDuplicate'));
                return prev;
            }
            return [...prev, { name, phoneNo: formatted }];
        });
    };

    // ContactMethodBottomSheet 에서 요청한 연락처 결과를 받는 네이티브 리스너
    useContact(addRecipient);

    const deleteRecipient = (index: number) => {
        setGiftRecipients((prev) => prev.filter((_, i) => i !== index));
    };

    /** 나에게 선물하기를 켜서 본인을 추가했으면 true 를 반환한다. */
    const toggleGiftToMe = () => {
        if (isGiftToMe) {
            setGiftRecipients((prev) => prev.filter((r) => toPhone(r.phoneNo) !== selfPhone));
            return false;
        }
        if (!selfPhone) {
            toast.error(t('giftOrder.phoneMissing'));
            return false;
        }
        if (!selfName) {
            toast.error(t('giftOrder.needSenderInfo'));
            return false;
        }
        addRecipient(selfName, selfPhone);
        return true;
    };

    return {
        giftRecipients,
        isGiftToMe,
        deleteRecipient,
        toggleGiftToMe,
    };
}

export default useGiftRecipients;
